import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type NotificationSettings } from '../lib/api';

/**
 * Email alerting, configured here rather than in the environment.
 *
 * The digest is deliberately silent when nothing is wrong — a daily message that
 * is almost always empty gets filtered, and then the one that matters lands in
 * the same folder. "Send a test" exists so a working configuration can still be
 * told apart from one that has simply never had anything to report.
 */
export function Notifications() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: api.notificationSettings,
  });

  const [form, setForm] = useState<NotificationSettings | null>(null);
  const [recipientText, setRecipientText] = useState('');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Seeded once from the server, then owned locally so typing is not fighting a
  // refetch.
  useEffect(() => {
    if (data && form === null) {
      setForm(data.settings);
      setRecipientText(data.settings.recipients.join(', '));
    }
  }, [data, form]);

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('not loaded');
      const recipients = recipientText
        .split(/[,;\s]+/)
        .map((value) => value.trim())
        .filter((value) => value !== '');

      return api.saveNotificationSettings({
        enabled: form.enabled,
        recipients,
        fromAddress: form.fromAddress,
        smtpHost: form.smtpHost,
        smtpPort: form.smtpPort,
        smtpSecure: form.smtpSecure,
        smtpUser: form.smtpUser,
        warnDaysAhead: form.warnDaysAhead,
        // Absent leaves the stored password alone; an empty string clears it.
        // Both have to be expressible or a password could never be removed.
        ...(clearPassword ? { smtpPassword: '' } : password !== '' ? { smtpPassword: password } : {}),
      });
    },
    onSuccess: (response) => {
      setPassword('');
      setClearPassword(false);
      setForm(response.settings);
      setRecipientText(response.settings.recipients.join(', '));
      setResult({ ok: true, message: 'Saved' });
      void queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const test = useMutation({
    mutationFn: api.sendTestNotification,
    onSuccess: setResult,
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const digest = useMutation({
    mutationFn: api.sendDigestNow,
    onSuccess: setResult,
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  if (isLoading || !form) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const set = <K extends keyof NotificationSettings>(key: K, value: NotificationSettings[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  const busy = save.isPending || test.isPending || digest.isPending;

  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <h3 style={{ marginTop: 0 }}>Email alerts</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        One message a day at 09:00 Eastern, and only when a channel needs attention — an
        authorization running out, a channel that cannot sync, or one that is failing. Nothing is
        sent when everything is fine.
      </p>

      {data?.configError && !save.isPending && (
        <div className="alert warn small">{data.configError}</div>
      )}
      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => set('enabled', event.target.checked)}
          />{' '}
          Send email alerts
        </label>
      </div>

      <div className="field">
        <label htmlFor="recipients">Recipients</label>
        <input
          id="recipients"
          value={recipientText}
          placeholder="lee@example.com, ops@example.com"
          onChange={(event) => setRecipientText(event.target.value)}
        />
        {/* More than one on purpose: a single address belonging to one person is
            how an alert goes unread for a fortnight. */}
        <span className="small muted">
          Comma or space separated. Worth adding a second address — one person on holiday should
          not mean nobody sees it.
        </span>
      </div>

      <div className="field">
        <label htmlFor="from">From address</label>
        <input
          id="from"
          value={form.fromAddress}
          placeholder="alerts@midwestfurniture.com"
          onChange={(event) => set('fromAddress', event.target.value)}
        />
      </div>

      <div className="field-row">
        <div className="field" style={{ flex: 2 }}>
          <label htmlFor="host">SMTP host</label>
          <input
            id="host"
            value={form.smtpHost}
            placeholder="smtp.example.com"
            onChange={(event) => set('smtpHost', event.target.value)}
          />
        </div>
        <div className="field" style={{ width: 90 }}>
          <label htmlFor="port">Port</label>
          <input
            id="port"
            type="number"
            value={form.smtpPort}
            onChange={(event) => set('smtpPort', Number(event.target.value))}
          />
        </div>
      </div>

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={form.smtpSecure}
            onChange={(event) => set('smtpSecure', event.target.checked)}
          />{' '}
          Implicit TLS
        </label>
        {/* The one SMTP setting people get wrong, so it says which is which
            rather than leaving it to be guessed. */}
        <span className="small muted">
          On for port 465. Leave off for 587, which starts plaintext and upgrades with STARTTLS
          automatically.
        </span>
      </div>

      <div className="field-row">
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="user">SMTP username</label>
          <input
            id="user"
            value={form.smtpUser}
            autoComplete="off"
            onChange={(event) => set('smtpUser', event.target.value)}
          />
          <span className="small muted">Leave blank if the relay authenticates by IP.</span>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="password">SMTP password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            disabled={clearPassword}
            placeholder={form.hasPassword ? '•••••••• (stored)' : ''}
            onChange={(event) => setPassword(event.target.value)}
          />
          {/* Write-only, like every other secret here: it goes in and is never
              read back, so the form cannot show what is stored. */}
          <span className="small muted">
            {form.hasPassword
              ? 'A password is stored. Leave blank to keep it.'
              : 'Not set. Stored encrypted, never shown again.'}
          </span>
          {form.hasPassword && (
            <label className="small">
              <input
                type="checkbox"
                checked={clearPassword}
                onChange={(event) => {
                  setClearPassword(event.target.checked);
                  if (event.target.checked) setPassword('');
                }}
              />{' '}
              remove the stored password
            </label>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="warn">Warn this many days before an authorization expires</label>
        <input
          id="warn"
          type="number"
          style={{ width: 90 }}
          value={form.warnDaysAhead}
          onChange={(event) => set('warnDaysAhead', Number(event.target.value))}
        />
        <span className="small muted">
          eBay's lasts 18 months and needs a person to re-consent, so noticing late is the whole
          risk. Etsy renews itself as long as sync keeps running.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="primary" disabled={busy} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button disabled={busy} onClick={() => test.mutate()}>
          {test.isPending ? 'Sending…' : 'Send a test'}
        </button>
        {/* Proves the real path rather than the test path — including staying
            quiet when there is nothing wrong. */}
        <button disabled={busy} onClick={() => digest.mutate()}>
          {digest.isPending ? 'Sending…' : 'Send the digest now'}
        </button>
      </div>
    </div>
  );
}
